import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Crest } from "@/components/Crest";
import { EcgLine } from "@/components/EcgLine";

const initial = { name: "", email: "", password: "", specialty: "", gmc_number: "", organization: "" };

const fields = [
  { key: "name", label: "Full Name & Title", type: "text", placeholder: "Dr Jane Smith" },
  { key: "email", label: "Professional Email", type: "email", placeholder: "j.smith@practice.nhs.uk" },
  { key: "password", label: "Password (min 8 characters)", type: "password", placeholder: "••••••••" },
  { key: "specialty", label: "Specialty", type: "text", placeholder: "Consultant Cardiologist" },
  { key: "gmc_number", label: "GMC / RCCP / HCPC Number", type: "text", placeholder: "GMC 1234567" },
  { key: "organization", label: "Organisation / Practice", type: "text", placeholder: "Kingsbridge Heart Centre" },
];

export default function Register() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-ivory" data-testid="register-page">
      <div className="hidden lg:flex lg:w-2/5 bg-navy relative flex-col justify-between p-14 overflow-hidden">
        <Link to="/" className="flex items-center" data-testid="register-brand-link">
          <Crest light className="h-12" />
        </Link>
        <div>
          <p className="font-serif italic text-3xl text-ivory/90 leading-snug max-w-md">
            “Membership is limited to verified clinicians.”
          </p>
          <EcgLine className="text-gold/70 h-12 w-72 mt-10" />
        </div>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/40">
          Applications reviewed within one working day
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 paper-grain">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-lg"
        >
          {done ? (
            <div data-testid="register-success" className="border border-gold/50 bg-white p-10">
              <Crest className="h-12" />
              <h1 className="font-serif text-3xl font-semibold text-navy mt-8 mb-4">
                Request received.
              </h1>
              <p className="text-sm text-navy/60 leading-relaxed mb-8">
                Your credentials will be verified by the CVI team and your account
                activated — typically within one working day. You will then be able to
                sign in to the specialist portal.
              </p>
              <Link
                to="/login"
                data-testid="register-success-login-link"
                className="inline-block bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold hover:text-navy transition-colors"
              >
                Return to Sign In
              </Link>
            </div>
          ) : (
            <>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-3">
                Specialist Portal
              </p>
              <h1 className="font-serif text-3xl font-semibold text-navy mb-8">Request access</h1>

              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="register-form">
                {fields.map((f) => (
                  <div key={f.key} className={f.key === "name" || f.key === "organization" ? "sm:col-span-2" : ""}>
                    <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2">
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      required
                      value={form[f.key]}
                      onChange={set(f.key)}
                      placeholder={f.placeholder}
                      data-testid={`register-${f.key.replace("_", "-")}-input`}
                      className="w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                ))}

                {error && (
                  <p data-testid="register-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-button"
                  className="sm:col-span-2 w-full bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
                >
                  {loading ? "Submitting…" : "Submit Access Request"}
                </button>
              </form>

              <p className="mt-8 text-sm text-navy/50">
                Already approved?{" "}
                <Link to="/login" data-testid="register-login-link" className="text-gold hover:text-navy transition-colors">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
